import { CommandBarButton, Spinner, SpinnerSize } from '@fluentui/react';
import styled from 'styled-components';
import { useContext, createContext, useEffect, useState, useMemo, useCallback } from 'react';

const SubmitButtonIconContainer = styled.div`
  width: 16px;
  height: 16px;
  margin: 0 4px;
  display: grid;
  place-content: center;
`;

interface SpinnerContextType {
  loadingState: Record<string, boolean>;
  setLoadingState: (key: string, isLoading: boolean) => void;
}

const SpinnerContext = createContext<SpinnerContextType>({
  loadingState: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setLoadingState: () => {}
});

export function SpinnerCmdBarButtonContextProvider({ children }) {
  const [loadingState, setLoadingStateInternal] = useState<Record<string, boolean>>({});

  const setLoadingState = useCallback(
    (key: string, isLoading: boolean) => {
      setLoadingStateInternal((prev) => ({
        ...prev,
        [key]: isLoading
      }));
    },
    [setLoadingStateInternal]
  );

  const value = useMemo(() => ({ loadingState, setLoadingState }), [loadingState, setLoadingState]);

  return <SpinnerContext.Provider value={value}>{children}</SpinnerContext.Provider>;
}

export function useSpinnerContext() {
  return useContext(SpinnerContext);
}

/** A command bar button that can replace its icon with a loading spinner when data.isLoading is true. */
export function SpinnerCommandBarButton(props) {
  const { loadingState } = useSpinnerContext();
  const buttonKey = props.key || props.id;
  const isLoading = useMemo(() => {
    if (typeof props.data?.isLoading === 'boolean') {
      return props.data?.isLoading;
    }

    if (typeof loadingState[buttonKey] === 'boolean') {
      return loadingState[buttonKey];
    }

    return false;
  }, [props.data?.isLoading, loadingState, buttonKey]);

  return (
    <CommandBarButton
      {...props}
      onRenderIcon={(props, defaultRender) => (
        <SubmitButtonIconContainer>
          {isLoading ? (
            <Spinner className="inner-spinner" size={SpinnerSize.small} />
          ) : (
            defaultRender(props)
          )}
        </SubmitButtonIconContainer>
      )}
    />
  );
}

/** Debounces true but immediately returns false */
export function useSpinnerCmdBarButtonDebouncedDisabled(disabled: boolean): [boolean, () => void] {
  const [value, setValue] = useState(disabled);

  useEffect(() => {
    const timeout = setTimeout(
      () => {
        setValue(disabled);
      },
      disabled ? 500 : 0
    );
    return () => clearTimeout(timeout);
  }, [disabled]);

  const forceUpdate = () => setValue(disabled);

  return [value, forceUpdate];
}
